import React, { useEffect, useState } from 'react';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { Link, RouteComponentProps } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import { useFavorites } from '../context/FavoritesContext';
import { useData } from '../context/DataContext';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useQueryStrings } from '../context/QueryStringContext';
import Popup from '../components/Popup';
import { useGlobalControls } from '../context/GlobalControlsContext';
import { useApp } from '../context/AppContext';

type NavigationProps = NavigationPropsBase & RouteComponentProps;

interface NavigationPropsBase {}

function Navigation(props: NavigationProps) {
  const { ...rest } = props;
  const { config } = useApp();

  const { updateQs, qs } = useQueryStrings();
  const { favorites } = useFavorites();
  const { data, todayLineup } = useData();
  const { toggleOverflow } = useGlobalControls();

  const [menuVisible, setMenuVisible] = useState<'nav' | 'lang' | 'none'>('none');

  useEffect(() => {
    toggleOverflow('navigation', menuVisible !== 'none');
  }, [menuVisible]);

  function scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // For a smooth scrolling animation
    });
  }

  const totalFavorites =
    Object.keys(favorites.events).length +
    Object.keys(favorites.venues).length +
    Object.keys(favorites.offers).length;

  const onBookmarkPage = location.pathname === '/bookmarks';

  if (qs.headless) {
    return <></>;
  }

  return (
    <>
      <div className="sticky" style={{ height: 77 }}>
        <div className="w-full py-2 bg-white flex items-center justify-center border-b border-slate-300">
          <Link
            onClick={() => {
              setMenuVisible('none');
              scrollToTop();
            }}
            to={{
              pathname: `/${config.visibleTabs[0] || ''}`,
              search: props.location.search,
            }}
          >
            <div className="nav-logo"></div>
          </Link>
          <Link
            className="flex-grow min-w-0 leading-none text-black"
            onClick={() => {
              setMenuVisible('none');
              scrollToTop();
            }}
            to={{
              pathname: `/${config.visibleTabs[0] || ''}`,
              search: props.location.search,
            }}
          >
            <div className="nav-text-1 text-lg font-semibold truncate"></div>
            <div className="nav-text-2 text-lg font-semibold truncate"></div>
          </Link>
          <div className="flex justify-center items-center mr-2" style={{ height: '60px' }}>
            {data.appConfig.LANGUAGES.length > 1 && (
              <Button
                className="py-3 px-2 nav-icon"
                onClick={() => setMenuVisible(menuVisible !== 'lang' ? 'lang' : 'none')}
              >
                <Icon icon={['fass', 'language']} size="xl" />
              </Button>
            )}
            <Link
              onClick={() => setMenuVisible('none')}
              to={{ pathname: '/bookmarks', search: props.location.search }}
              className="py-3 px-2 text-black nav-icon"
            >
              <Icon
                icon={['far', 'heart']}
                size="xl"
                showBubble={totalFavorites != 0 && !onBookmarkPage}
                bubbleClassName="bg-blue-500 nav-bubble"
                bubbleText={String(totalFavorites)}
              />
            </Link>
            <Button
              onClick={() => setMenuVisible(menuVisible !== 'nav' ? 'nav' : 'none')}
              className="py-3 px-2 nav-icon"
            >
              <Icon
                icon={['fass', 'bars']}
                size="xl"
                showBubble={todayLineup.messages.length > 0}
                bubbleText={String(todayLineup.messages.length)}
                bubbleClassName="bg-blue-500 nav-bubble"
              />
            </Button>
          </div>
        </div>
      </div>
      {menuVisible === 'nav' && (
        <Popup onClose={() => setMenuVisible('none')} hideTopBlur>
          <div className="flex flex-col justify-evenly text-lg font-semibold w-full bg-white py-1 border-b border-slate-300">
            {config.visibleTabs.includes('lineup') && (
              <NavMenuItem
                {...rest}
                icon={['far', 'calendar-day']}
                className="py-3 px-4 text-black nav-icon"
                name={data.appConfig.LINEUP_LABEL}
                to="/lineup"
                toggleNavMenu={() => setMenuVisible('none')}
              />
            )}
            {config.visibleTabs.includes('explore') && (
              <NavMenuItem
                {...rest}
                icon={['far', 'compass']}
                className="py-3 px-4 text-black nav-icon"
                name={data.appConfig.EXPLORE_LABEL}
                to="/explore"
                toggleNavMenu={() => setMenuVisible('none')}
              />
            )}
            {config.visibleTabs.includes('offers') && (
              <NavMenuItem
                {...rest}
                icon={['far', 'hand-holding-circle-dollar']}
                className="py-3 px-4 text-black nav-icon"
                name={data.appConfig.OFFERS_LABEL}
                to="/offers"
                toggleNavMenu={() => setMenuVisible('none')}
              />
            )}
            {config.visibleTabs.includes('messages') && (
              <NavMenuItem
                {...rest}
                icon={['far', 'message-dots']}
                className="py-3 px-4 text-black nav-icon"
                name={data.appConfig.MESSAGES_LABEL}
                to="/messages"
                toggleNavMenu={() => setMenuVisible('none')}
                bubbleClassName="bg-blue-500 nav-bubble"
                showBubble={todayLineup.messages.length > 0}
                bubbleText={String(todayLineup.messages.length)}
              />
            )}
            {config.visibleTabs.includes('information') && (
              <NavMenuItem
                {...rest}
                icon={['far', 'info-circle']}
                className="py-3 px-4 text-black nav-icon"
                name={data.appConfig.INFORMATION_LABEL}
                to="/information"
                toggleNavMenu={() => setMenuVisible('none')}
              />
            )}
          </div>
        </Popup>
      )}
      {menuVisible === 'lang' && (
        <Popup onClose={() => setMenuVisible('none')} hideTopBlur>
          <div className="flex flex-col justify-evenly text-lg font-semibold w-full bg-white py-1 border-b border-slate-300">
            {data.appConfig.LANGUAGES.map(l => (
              <Button
                className="py-3 px-4 font-semibold flex items-center"
                onClick={() => {
                  updateQs('lang', l.key);
                  setMenuVisible('none');
                }}
              >
                {l.label}
              </Button>
            ))}
          </div>
        </Popup>
      )}
    </>
  );
}

interface NavMenuItemPropsBase {
  icon: IconProp;
  name: string;
  to: string;
  toggleNavMenu: () => void;
  showBubble?: boolean;
  bubbleClassName?: string;
  bubbleText?: string;
}

type NavMenuItemProps = React.HTMLAttributes<HTMLDivElement> &
  NavMenuItemPropsBase &
  RouteComponentProps;

function NavMenuItem(props: NavMenuItemProps) {
  const { icon, name, to, toggleNavMenu, showBubble, bubbleClassName, bubbleText, className } =
    props;

  const classes = ['flex items-center w-full'];

  if (className) {
    classes.push(className);
  }

  return (
    <Link
      to={{ pathname: to, search: props.location.search }}
      onClick={toggleNavMenu}
      className={classes.join(' ')}
    >
      <Icon
        icon={icon}
        showBubble={showBubble}
        bubbleClassName={bubbleClassName}
        bubbleText={bubbleText}
      />
      <div className="ml-2">{name}</div>
    </Link>
  );
}

export default Navigation;
